/**
 * Reel Card Component
 * Displays individual Instagram reel video with play/pause controls
 */

import { motion } from 'framer-motion'
import { useState, useRef, useEffect } from 'react'

export default function ReelCard({ reel, index, playingReelId, onPlayRequest }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false) // Start unmuted for sound
  const [showControls, setShowControls] = useState(true)
  const [videoError, setVideoError] = useState(false)
  const videoRef = useRef(null)
  const controlsTimeoutRef = useRef(null)

  const handlePlayPause = (e) => {
    e.stopPropagation()
    if (!videoRef.current) return

    if (isPlaying) {
      onPlayRequest?.(null)
      videoRef.current.pause()
      setIsPlaying(false)
    } else {
      onPlayRequest?.(reel.id)
      videoRef.current.play()
      setIsPlaying(true)
    }
    // Show controls briefly when clicked
    setShowControls(true)
    resetControlsTimeout()
  }

  const handleMuteToggle = (e) => {
    e.stopPropagation()
    if (!videoRef.current) return
    
    videoRef.current.muted = !isMuted
    setIsMuted(!isMuted)
    setShowControls(true)
    resetControlsTimeout()
  }

  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    // Hide controls after 3 seconds of inactivity
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false)
      }
    }, 3000)
  }

  // Pause this video when another video starts playing
  useEffect(() => {
    if (!videoRef.current || !reel.videoUrl || !onPlayRequest) return
    if (playingReelId !== null && playingReelId !== reel.id && isPlaying) {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }, [playingReelId, reel.id, reel.videoUrl, isPlaying, onPlayRequest])

  useEffect(() => {
    if (!videoRef.current) return

    const video = videoRef.current

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleMouseMove = () => {
      setShowControls(true)
      resetControlsTimeout()
    }

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('mousemove', handleMouseMove)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('mousemove', handleMouseMove)
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current)
      }
    }
  }, [isPlaying])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
      className="aspect-[9/16] bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg md:rounded-xl border border-pitch-green/20 overflow-hidden hover:border-pitch-green/40 transition-all group relative shadow-lg hover:shadow-pitch-green/20"
      onMouseMove={() => {
        setShowControls(true)
        resetControlsTimeout()
      }}
      onMouseLeave={() => {
        if (isPlaying) {
          setShowControls(false)
        }
      }}
    >
      {/* Video Player - prefer videoUrl over youtubeId to avoid overlap */}
      {reel.videoUrl && !videoError && (
        <>
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            loop
            muted={isMuted}
            playsInline
            preload="metadata"
            poster={reel.thumbnail}
            onClick={handlePlayPause}
            onError={() => setVideoError(true)}
            crossOrigin="anonymous"
          >
            <source src={reel.videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          
          {/* Thumbnail Overlay - Shows when paused and thumbnail exists */}
          {!isPlaying && reel.thumbnail && (
            <div 
              className="absolute inset-0 z-10 bg-cover bg-center"
              style={{ backgroundImage: `url(${reel.thumbnail})` }}
            >
              {/* Dark overlay for better contrast */}
              <div className="absolute inset-0 bg-stadium-black/30"></div>
              
              {/* Content overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                <p className="text-pitch-green text-sm md:text-base font-bold mb-1">{reel.title}</p>
                <p className="text-gray-300 text-xs md:text-sm">{reel.description}</p>
              </div>
            </div>
          )}
          
          {/* Fallback thumbnail if no custom thumbnail but video exists */}
          {!isPlaying && !reel.thumbnail && (
            <div className="absolute inset-0 z-10 bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 flex flex-col items-center justify-center p-4 text-center">
              <div className="text-4xl md:text-5xl mb-3">🎬</div>
              <p className="text-pitch-green text-sm md:text-base font-bold mb-1">{reel.title}</p>
              <p className="text-gray-300 text-xs md:text-sm">{reel.description}</p>
            </div>
          )}
        </>
      )}

      {/* YouTube Shorts Embed - only when no videoUrl or video failed */}
      {reel.youtubeId && (!reel.videoUrl || videoError) && (
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${reel.youtubeId}?autoplay=0&mute=1&loop=1&playlist=${reel.youtubeId}`}
          title={reel.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}

      {/* Play/Pause Button - Top Left Corner */}
      {reel.videoUrl && (
        <div 
          className={`absolute top-3 left-3 z-30 transition-opacity duration-300 ${
            showControls || !isPlaying ? 'opacity-100' : 'opacity-70'
          }`}
          onClick={handlePlayPause}
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="w-8 h-8 md:w-9 md:h-9 bg-stadium-black/80 hover:bg-stadium-black/90 rounded-full flex items-center justify-center backdrop-blur-sm border border-pitch-green/30 transition-all"
            aria-label={isPlaying ? 'Pause video' : 'Play video'}
          >
            {isPlaying ? (
              // Pause Icon
              <svg className="w-4 h-4 md:w-5 md:h-5 text-pitch-green" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              // Play Icon
              <svg className="w-4 h-4 md:w-5 md:h-5 text-pitch-green ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </motion.button>
        </div>
      )}

      {/* Mute/Unmute Button - Top Right Corner */}
      {reel.videoUrl && (
        <div 
          className={`absolute top-3 right-3 z-30 transition-opacity duration-300 ${
            showControls || isMuted || !isPlaying ? 'opacity-100' : 'opacity-70'
          }`}
          onClick={handleMuteToggle}
        >
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="w-8 h-8 md:w-9 md:h-9 bg-stadium-black/80 hover:bg-stadium-black/90 rounded-full flex items-center justify-center backdrop-blur-sm border border-pitch-green/30 transition-all"
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          >
            {isMuted ? (
              // Muted Icon
              <svg className="w-4 h-4 md:w-5 md:h-5 text-pitch-green" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
              </svg>
            ) : (
              // Unmuted Icon
              <svg className="w-4 h-4 md:w-5 md:h-5 text-pitch-green" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
              </svg>
            )}
          </motion.button>
        </div>
      )}

      {/* Fallback Content (if no video, or video failed and no YouTube) */}
      {!reel.youtubeId && (!reel.videoUrl || videoError) && (
        <div className="w-full h-full flex flex-col items-center justify-center relative z-10 p-4 bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800">
          {/* Thumbnail if available */}
          {reel.thumbnail && (
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-50"
              style={{ backgroundImage: `url(${reel.thumbnail})` }}
            >
              <div className="absolute inset-0 bg-stadium-black/60"></div>
            </div>
          )}
          
          <div className="relative z-10 text-center">
            {videoError && (
              <p className="text-red-400 text-xs mb-2">Video couldn&apos;t load</p>
            )}
            <motion.div
              className="text-5xl mb-3"
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                delay: index * 0.5
              }}
            >
              🎬
            </motion.div>
            <p className="text-pitch-green text-sm md:text-base font-bold mb-1">{reel.title}</p>
            <p className="text-gray-300 text-xs md:text-sm text-center px-2">{reel.description}</p>
          </div>
        </div>
      )}
      
      {/* Bottom label */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-stadium-black/95 to-transparent px-2 py-2 md:px-3 md:py-3 text-center z-20">
        <p className="text-pitch-green text-[10px] md:text-xs font-bold">INSTAGRAM REEL</p>
        <p className="text-gray-500 text-[9px] md:text-[10px]">9:16 Format</p>
        {!isMuted && isPlaying && (
          <p className="text-pitch-green text-[8px] md:text-[9px] mt-0.5 flex items-center justify-center gap-1">
            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
            Sound On
          </p>
        )}
      </div>
    </motion.div>
  )
}
